import { useCallback, useEffect, useRef, useState } from "react";
import { PackageSearch, CheckCircle2, Circle, Truck, Boxes, ClipboardList, Bell, Camera, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import EditarPedidoDialog from "@/components/traspasos/EditarPedidoDialog";

const PASOS = [
  { id: "pendiente", label: "Solicitado", Icon: ClipboardList },
  { id: "preparando", label: "Preparando (RubiNova)", Icon: Boxes },
  { id: "en_transito", label: "En tránsito", Icon: Truck },
  { id: "recibido", label: "Recibido", Icon: CheckCircle2 },
];
const AVISO = { preparando: "RubiNova está surtiendo tu pedido", en_transito: "Tu pedido va en camino", recibido: "Pedido recibido", con_incidencia: "Pedido recibido con incidencia" };
const fecha = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null);
const HUB_LABEL = { en_cola: "En cola", convertida: "Convertida a pedido", asignada: "Asignada", en_proceso: "Surtiendo (picking)", pausa: "En pausa", empacado: "Empacada · lista para envío", empacada: "Empacada · lista para envío", despachado: "Despachada · en camino", despachada: "Despachada · en camino", enviado: "Enviada · en camino", enviada: "Enviada · en camino", en_ruta: "En ruta", en_camino: "En camino", completada: "Completada", descartada: "Descartada", recibido: "Recibida en sucursal" };
const HUB_LISTA = new Set(["empacado", "despachado", "completada"]);

/** Avisos en vivo (polling 30 s): toast cuando RubiNova cambia el estado de un pedido de la sucursal. */
export function useAvisosPedido(onCambio) {
  const vistos = useRef(new Set());
  useEffect(() => {
    let vivo = true;
    async function tick() {
      try {
        const { data } = await api.get("/pedidos/avisos");
        if (!vivo) return;
        const nuevos = data.filter((a) => !vistos.current.has(`${a.traspaso_id}:${a.estado}`));
        nuevos.forEach((a) => { vistos.current.add(`${a.traspaso_id}:${a.estado}`); toast.info(`${a.folio}: ${AVISO[a.estado] || a.estado}${a.nota ? ` · ${a.nota}` : ""}`, { duration: 8000 }); });
        if (nuevos.length) { await api.post("/pedidos/avisos/leidos", { traspaso_ids: nuevos.map((a) => a.traspaso_id) }); onCambio?.(); }
      } catch { /* silencioso */ }
    }
    tick();
    const id = setInterval(tick, 30000);
    return () => { vivo = false; clearInterval(id); };
  }, [onCambio]);
}

function HubEstado({ rn }) {
  if (!rn || !rn.hub_status) return null;
  const pp = rn.picking_progress || {};
  const pct = typeof pp.percent === "number" ? pp.percent : (pp.total_items ? Math.round((pp.prepared_items || 0) / pp.total_items * 100) : 0);
  const lista = HUB_LISTA.has(rn.hub_status);
  return (
    <div className="w-full mt-1 rounded-xl bg-brand-ink text-white px-3 py-2" data-testid={`hub-${rn.order_folio || rn.external_id || "rep"}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] font-semibold flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${lista ? "bg-emerald-400" : "bg-brand-amber animate-pulse"}`} /> RubiNOVA: {HUB_LABEL[rn.hub_status] || rn.hub_status}</span>
        {rn.order_folio && <span className="text-[10px] font-mono text-brand-amber">{rn.order_folio}</span>}
      </div>
      {pp.total_items > 0 && (
        <div className="mt-1.5">
          <div className="flex justify-between text-[9px] text-white/70 mb-0.5"><span>Surtido {pp.prepared_items || 0}/{pp.total_items}</span><span className="font-mono">{pct}%</span></div>
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-brand-amber transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      {lista && <div className="text-[10px] text-emerald-300 mt-1 font-semibold">✔ Lista para envío</div>}
    </div>
  );
}

function Evidencia({ ev }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/archivos/${ev.path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [ev.path]);
  if (!url) return <div className="w-16 h-16 rounded-lg bg-brand-surface animate-pulse" />;
  if (ev.tipo === "video") return <video src={url} controls preload="metadata" className="w-28 h-16 rounded-lg object-cover border border-brand-line bg-black" data-testid="evidencia-video" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={ev.nombre || "recepción"} className="w-16 h-16 rounded-lg object-cover border border-brand-line hover:opacity-80 transition-opacity" data-testid="evidencia-imagen" /></a>;
}

function GaleriaRecepcion({ evidencias, folio }) {
  return (
    <div className="w-full flex flex-wrap items-center gap-2" data-testid={`foto-recepcion-${folio}`}>
      <span className="text-[11px] text-brand-slate flex items-center gap-1 w-full"><Camera className="w-3 h-3 text-brand-amber" /> Evidencia de recepción · {evidencias.length} archivo{evidencias.length !== 1 ? "s" : ""}</span>
      {evidencias.map((ev) => <Evidencia key={ev.path} ev={ev} />)}
    </div>
  );
}

function Timeline({ p }) {
  return (
    <ol className="flex items-center gap-1 flex-1 min-w-[260px]" data-testid={`seguimiento-timeline-${p.folio}`}>
      {PASOS.map((s, i) => {
        const hecho = i <= p.paso_actual; const actual = i === p.paso_actual;
        const incid = s.id === "recibido" && p.estado === "con_incidencia";
        return (
          <li key={s.id} className="flex items-center gap-1 flex-1 last:flex-none">
            <div className="flex flex-col items-center min-w-[72px]">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${incid ? "bg-rose-600 border-rose-600 text-white" : hecho ? "bg-brand-ink border-brand-ink text-brand-amber" : "border-brand-line text-brand-line"} ${actual && !hecho ? "" : ""} ${actual && s.id !== "recibido" ? "ring-4 ring-brand-amber/30 animate-pulse" : ""}`} data-testid={`paso-${p.folio}-${s.id}`}>{hecho ? <s.Icon className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}</div>
              <div className={`text-[10px] mt-1 text-center leading-tight ${hecho ? "text-brand-ink font-semibold" : "text-brand-muted"}`}>{incid ? "Con incidencia" : s.label}</div>
              <div className="text-[9px] text-brand-muted font-mono">{fecha(p.tiempos?.[s.id]) || ""}</div>
            </div>
            {i < PASOS.length - 1 && <div className={`h-0.5 flex-1 ${i < p.paso_actual ? "bg-brand-ink" : "bg-brand-line"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

export default function SeguimientoPedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [editar, setEditar] = useState(null);
  const cargar = useCallback(() => api.get("/pedidos/seguimiento").then((r) => setPedidos(r.data)).catch(() => {}), []);
  useEffect(() => { cargar(); }, [cargar]);
  useAvisosPedido(cargar);
  const activos = pedidos.filter((p) => !["recibido", "con_incidencia"].includes(p.estado));
  return (
    <section className="surface-card p-5 space-y-4" data-testid="seguimiento-pedidos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h2 className="font-display font-bold text-brand-ink flex items-center gap-2"><PackageSearch className="w-4 h-4 text-brand-amber" /> Seguimiento de mis pedidos</h2><p className="text-xs text-brand-muted">Solicitado → Preparando (RubiNova) → En tránsito → Recibido. Recibirás un aviso en pantalla en cada cambio.</p></div>
        <span className="chip chip-amber" data-testid="seguimiento-activos"><Bell className="w-3 h-3" /> {activos.length} en curso</span>
      </div>
      <div className="space-y-3">
        {pedidos.map((p) => (
          <div key={p.id} className="rounded-2xl border border-brand-line/70 p-3 flex flex-wrap items-center gap-4 hover:bg-brand-canvas/60 transition-colors" data-testid={`seguimiento-row-${p.folio}`}>
            <div className="min-w-[150px]">
              <div className="font-mono font-bold text-brand-ink">{p.folio}</div>
              <div className="text-[11px] text-brand-muted">{p.items_count} insumos · ${p.subtotal.toFixed(2)}</div>
              <div className="text-[10px] text-brand-muted">{p.origen_nombre} → {p.destino_nombre}</div>
              {(p.guia || p.rubinova?.tracking) && <div className="text-[10px] font-mono text-brand-slate" data-testid={`seg-guia-${p.folio}`}>Guía {p.guia || p.rubinova.tracking}{p.rubinova?.paqueteria ? ` · ${p.rubinova.paqueteria}` : ""}</div>}
              {p.prepara_nombre && <div className="text-[10px] text-brand-slate">Surte: {p.prepara_nombre}</div>}
            </div>
            <Timeline p={p} />
            <HubEstado rn={p.rubinova} />
            {p.estado === "pendiente" && !(p.rubinova?.hub_status && !["en_cola", "pendiente", "nueva", "nuevo"].includes(p.rubinova.hub_status)) && <button data-testid={`seguimiento-editar-${p.folio}`} onClick={() => setEditar(p)} className="btn-soft h-8 px-3 text-xs"><Pencil className="w-3.5 h-3.5" /> Editar / cancelar</button>}
            {p.estado === "preparando" && <span className="chip chip-ink text-[10px]" title="El almacén ya está surtiendo; ya no se puede editar">En preparación · sin cambios</span>}
            {(p.evidencias_recepcion?.length || p.foto_recepcion) && <GaleriaRecepcion evidencias={p.evidencias_recepcion?.length ? p.evidencias_recepcion : [{ path: p.foto_recepcion, tipo: "imagen" }]} folio={p.folio} />}
          </div>
        ))}
        {pedidos.length === 0 && <p className="text-sm text-brand-muted text-center py-6">Aún no hay pedidos de reposición para tu sucursal.</p>}
      </div>
      <EditarPedidoDialog pedido={editar} onClose={() => setEditar(null)} onSaved={cargar} />
    </section>
  );
}
